import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import {
  MessengerChat,
  mapHandshakeMessageToChatMessage,
} from "@/components/chat";
import type { HandshakeSessionPublicDetail } from "@/types";
import { channelLabel } from "./config";
import { ClosedConversationNotice } from "./ClosedConversationNotice";

interface HandshakeSellerChatProps {
  session: HandshakeSessionPublicDetail;
  canInteract: boolean;
  isSending: boolean;
  onSend: (message: string) => Promise<void>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Hide MessengerChat header when outer SectionCard provides chrome. */
  hideHeader?: boolean;
  /** Extra content after the closed notice / above composer (e.g. reopen). */
  afterFooter?: ReactNode;
  className?: string;
  minHeightClassName?: string;
}

export function HandshakeSellerChat({
  session,
  canInteract,
  isSending,
  onSend,
  onRefresh,
  isRefreshing,
  hideHeader = false,
  afterFooter,
  className,
  minHeightClassName,
}: HandshakeSellerChatProps) {
  const { t } = useTranslation(["bookings", "handshakePages", "handshake"]);
  const isActive = session.status === "PENDING";
  const customerName =
    `${session.customer.first_name} ${session.customer.last_name}`.trim();
  const messages = session.messages.map((message) =>
    mapHandshakeMessageToChatMessage(message, {
      sellerView: true,
      outboundCustomerLabel: t("handshakePages:outboundCustomerLabel"),
    }),
  );

  const closedNotice =
    !isActive || !canInteract ? (
      <div className="space-y-3">
        <ClosedConversationNotice status={session.status} />
        {afterFooter}
      </div>
    ) : (
      afterFooter
    );

  return (
    <MessengerChat
      title={customerName}
      subtitle={t("bookings:detail.chatViaChannel", {
        channel: channelLabel(session.customer_channel),
      })}
      hideHeader={hideHeader}
      messages={messages}
      emptyText={t("handshakePages:conversationEmpty")}
      onRefresh={hideHeader ? undefined : onRefresh}
      isRefreshing={isRefreshing}
      blockedLabel={t("handshakePages:messageBlocked")}
      footer={
        !isActive || !canInteract
          ? closedNotice
            ? { kind: "closed", node: closedNotice }
            : undefined
          : {
              kind: "input",
              onSend,
              isSending,
              placeholder: t("handshakePages:replyPlaceholder"),
              sendLabel: t("handshake:messageInput.send"),
            }
      }
      className={className}
      minHeightClassName={minHeightClassName}
    />
  );
}
